import { http } from '@/api/http';

/**
 * Admin catalogue of consent-type definitions (BE story PTV2-1179 / S1 of the
 * PTV2-1090 epic). Hand-declared because the shared `openapi.yaml` in ai-hub
 * hasn't been rev'd against the shipped BE spec yet — see PTV2-1208.
 *
 * Wire shape is **snake_case throughout** — the BE's ObjectMapper uses the
 * Jackson SNAKE_CASE naming strategy tenant-wide (same as the consents DTO;
 * see api/consents.ts). Springdoc reports the Java field names in the
 * OpenAPI export (`sortOrder`, `createdAt`), which is misleading — sending
 * camelCase gets rejected by bean-validation once the mapper produces a
 * null Java field.
 */
export interface ConsentTypeDefDto {
  readonly id?: string;
  readonly name: string;
  readonly description?: string | null;
  readonly sort_order?: number | null;
  readonly is_required?: boolean;
  readonly is_active?: boolean;
  readonly created_at?: string;
  readonly created_by?: string;
  readonly updated_at?: string;
  readonly updated_by?: string;
}

/** RFC 6902 JSON-Patch operation. */
export interface JsonPatchOp {
  readonly op: 'add' | 'remove' | 'replace' | 'move' | 'copy' | 'test';
  readonly path: string;
  readonly value?: unknown;
  readonly from?: string;
}

export type ConsentTypeActiveFilter = 'true' | 'false' | 'all';

export const adminConsentTypesEndpoints = {
  /**
   * GET /consent-types — tenant-scoped list.
   * Default (no param) returns active-only; pass `active: 'all'` for both.
   */
  listConsentTypes: (
    params?: { active?: ConsentTypeActiveFilter; signal?: AbortSignal },
  ): Promise<ConsentTypeDefDto[]> => {
    const query = new URLSearchParams();
    if (params?.active !== undefined) query.set('active', params.active);
    const suffix = query.toString();
    const path = suffix.length > 0 ? `/consent-types?${suffix}` : '/consent-types';
    return http.get<ConsentTypeDefDto[]>(
      path,
      params?.signal !== undefined ? { signal: params.signal } : undefined,
    );
  },

  /** POST /consent-types — create; 409 on duplicate name. */
  createConsentType: (
    body: ConsentTypeDefDto,
    options?: { signal?: AbortSignal },
  ): Promise<ConsentTypeDefDto> =>
    http.post<ConsentTypeDefDto>('/consent-types', body, options),

  /** GET /consent-types/{guid}. */
  getConsentType: (
    guid: string,
    options?: { signal?: AbortSignal },
  ): Promise<ConsentTypeDefDto> =>
    http.get<ConsentTypeDefDto>(
      `/consent-types/${encodeURIComponent(guid)}`,
      options,
    ),

  /**
   * PATCH /consent-types/{guid} — RFC 6902 JSON-Patch over `name`,
   * `description`, `is_required`, `sort_order`, `is_active`.
   * Soft-disable is `replace /is_active false`.
   */
  patchConsentType: (
    guid: string,
    ops: readonly JsonPatchOp[],
    options?: { signal?: AbortSignal },
  ): Promise<ConsentTypeDefDto> =>
    http.patch<ConsentTypeDefDto>(
      `/consent-types/${encodeURIComponent(guid)}`,
      ops,
      {
        headers: { 'Content-Type': 'application/json-patch+json' },
        ...(options?.signal !== undefined ? { signal: options.signal } : {}),
      },
    ),
};
